import { css } from "@emotion/css";
import { useTheme } from "@emotion/react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../utils/firebase";
import { SideBar } from "../components/nav/sidebar-nav";
import { useStyleInjector } from "../hooks/useStyleInjector";
import { useAuth } from "../providers/auth.provider";
import { usePurchases } from "../hooks/use-purchases";
import { useCheckout } from "../hooks/use-checkout";
import { useContent } from "../providers/content.provider";
import { ContextualizedUnit } from "../../shared/unit.types";

export const UserRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  const theme = useTheme();

  useStyleInjector`
  body {
      background: linear-gradient(270deg, #000000, #484d4c);
      background-size: 400% 400%;

      -webkit-animation: GradientAnimation 15s ease infinite;
      -moz-animation: GradientAnimation 15s ease infinite;
      animation: GradientAnimation 15s ease infinite;
  }

  @-webkit-keyframes GradientAnimation {
      0%{background-position:0% 50%}
      50%{background-position:100% 50%}
      100%{background-position:0% 50%}
  }
  @-moz-keyframes GradientAnimation {
      0%{background-position:0% 50%}
      50%{background-position:100% 50%}
      100%{background-position:0% 50%}
  }
  @keyframes GradientAnimation {
      0%{background-position:0% 50%}
      50%{background-position:100% 50%}
      100%{background-position:0% 50%}
  }`;

  return (
    <>
      <SideBar
        currPage="user"
        className={css`
          background: transparent;
        `}
      />
      <main
        className={css`
          width: 100%;
          padding: 32px 24px;
          overflow-y: auto;
        `}
      >
        {loading ? (
          <Spinner />
        ) : isAuthenticated ? (
          <LoggedInView />
        ) : (
          <LoggedOutView />
        )}
      </main>
    </>
  );
};

const Spinner = () => (
  <div
    className={css`
      display: flex;
      justify-content: center;
      align-items: center;
      height: 200px;
    `}
  >
    <div
      className={css`
        width: 40px;
        height: 40px;
        border: 3px solid rgba(255, 255, 255, 0.3);
        border-radius: 50%;
        border-top-color: #fff;
        animation: spin 1s ease-in-out infinite;

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}
    />
  </div>
);

// ---------- Firestore session fetch ----------

type RawSession = {
  id: string;
  completed_at: Date | null;
  started_at: Date | null;
};

function useProfileSessions(uid: string | undefined) {
  const [sessions, setSessions] = useState<RawSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setSessions([]);
      setLoading(false);
      return;
    }
    const ref = collection(db, "users", uid, "sessions");
    getDocs(ref).then((snap) => {
      const results: RawSession[] = snap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id.replace(/\|/g, "/"),
          completed_at: data.completed_at?.toDate?.() ?? null,
          started_at: data.started_at?.toDate?.() ?? null,
        };
      });
      setSessions(results);
      setLoading(false);
    });
  }, [uid]);

  return { sessions, loading };
}

// ---------- Derive unit URL from contextualized unit ----------

function unitUrl(unit: ContextualizedUnit): string {
  const { section } = unit;
  const course = section.course;
  const ecosystem = course.ecosystem;
  return `/${ecosystem.slug}/${course.slug}/${section.slug}/${unit.id}`;
}

// ---------- Logged In View ----------

const LoggedInView = () => {
  const { user, signOut } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const purchases = usePurchases();
  const { startCheckout, loading: checkoutLoading } = useCheckout();
  const { ecosystems } = useContent();
  const { sessions, loading: sessionsLoading } = useProfileSessions(
    user?.uid
  );

  const handleLogout = async () => {
    await signOut();
    window.location.href = "/";
  };

  // Flatten all units
  const allUnits: ContextualizedUnit[] = ecosystems.flatMap((eco) =>
    eco.courses.flatMap((course) =>
      course.sections.flatMap((section) => section.units)
    )
  );

  // Map slug → unit for purchased unit lookups
  // Slug format matches unit.provider.tsx: ecosystem/course/section/unit.name
  const unitsBySlug = new Map<string, ContextualizedUnit>(
    allUnits.map((u) => [
      `${u.section.course.ecosystem.slug}/${u.section.course.slug}/${u.section.slug}/${u.name}`,
      u,
    ])
  );

  // Build a set of completed session IDs
  const completedIds = new Set(
    sessions
      .filter((s) => s.completed_at != null)
      .map((s) => s.id)
  );
  const startedIds = new Set(sessions.map((s) => s.id));

  const completedUnits = allUnits.filter((u) => {
    const id = `${u.section.course.ecosystem.slug}/${u.section.course.slug}/${u.section.slug}/${u.id}`;
    return completedIds.has(id);
  });

  const incompleteUnits = allUnits.filter((u) => {
    const id = `${u.section.course.ecosystem.slug}/${u.section.course.slug}/${u.section.slug}/${u.id}`;
    return startedIds.has(id) && !completedIds.has(id);
  });

  // Determine plan label
  let planLabel = "Free";
  let planDescription = "Access to free units only.";
  if (purchases.unlockedAll && purchases.progressSaving) {
    planLabel = "Premium";
    planDescription = "All units unlocked + progress saving.";
  } else if (purchases.unlockedAll) {
    planLabel = "All Units Unlocked";
    planDescription = "Full access to all units.";
  } else if (purchases.progressSaving) {
    planLabel = "Progress Saving";
    planDescription = "Your progress is saved across sessions.";
  }

  const cardStyle = css`
    background: rgba(30, 30, 30, 0.7);
    backdrop-filter: blur(10px);
    border-radius: 10px;
    border: 1px solid rgba(100, 100, 100, 0.2);
    padding: 24px;
    margin-bottom: 20px;
  `;

  const sectionTitle = css`
    margin: 0 0 16px 0;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${theme.colors.grey50};
    font-weight: 600;
  `;

  return (
    <div
      className={css`
        max-width: 860px;
        margin: 0 auto;
      `}
    >
      {/* Header */}
      <div
        className={css`
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 28px;
        `}
      >
        {user?.photoURL ? (
          <img
            src={user.photoURL}
            alt="Profile"
            referrerPolicy="no-referrer"
            className={css`
              width: 64px;
              height: 64px;
              border-radius: 50%;
              object-fit: cover;
              flex-shrink: 0;
            `}
          />
        ) : (
          <div
            className={css`
              width: 64px;
              height: 64px;
              border-radius: 50%;
              background-color: ${theme.colors.tsBlue};
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 26px;
              color: white;
              font-weight: 600;
              flex-shrink: 0;
            `}
          >
            {user?.email?.charAt(0).toUpperCase() || "?"}
          </div>
        )}
        <div>
          <h1
            className={css`
              margin: 0 0 4px 0;
              font-size: 22px;
              color: ${theme.colors.grey100};
            `}
          >
            {user?.displayName || user?.email?.split("@")[0] || "there"}
          </h1>
          <p
            className={css`
              margin: 0;
              font-size: 14px;
              color: ${theme.colors.grey50};
            `}
          >
            {user?.email}
          </p>
        </div>
        <div className={css` margin-left: auto; `}>
          <button
            onClick={handleLogout}
            className={css`
              padding: 8px 16px;
              background: transparent;
              color: ${theme.colors.grey50};
              border: 1px solid ${theme.colors.grey35};
              border-radius: 6px;
              font-size: 13px;
              cursor: pointer;
              transition: all 0.15s;
              &:hover {
                color: ${theme.colors.grey100};
                border-color: ${theme.colors.grey50};
              }
            `}
          >
            Log Out
          </button>
        </div>
      </div>

      {/* Plan */}
      <div className={cardStyle}>
        <h2 className={sectionTitle}>Your Plan</h2>
        {purchases.loading ? (
          <p className={css` color: ${theme.colors.grey50}; margin: 0; `}>Loading...</p>
        ) : (
          <div
            className={css`
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 20px;
              flex-wrap: wrap;
            `}
          >
            <div>
              <span
                className={css`
                  display: inline-block;
                  padding: 4px 12px;
                  border-radius: 20px;
                  font-size: 13px;
                  font-weight: 600;
                  margin-bottom: 6px;
                  background: ${
                    planLabel === "Free"
                      ? theme.colors.grey25
                      : planLabel === "Premium"
                      ? "rgba(60, 199, 146, 0.15)"
                      : "rgba(15, 178, 255, 0.15)"
                  };
                  color: ${
                    planLabel === "Free"
                      ? theme.colors.grey70
                      : planLabel === "Premium"
                      ? theme.colors.success50
                      : theme.colors.tsBlue
                  };
                  border: 1px solid ${
                    planLabel === "Free"
                      ? theme.colors.grey35
                      : planLabel === "Premium"
                      ? theme.colors.success20
                      : "rgba(15, 178, 255, 0.3)"
                  };
                `}
              >
                {planLabel}
              </span>
              <p className={css` margin: 0; font-size: 14px; color: ${theme.colors.grey70}; `}>
                {planDescription}
              </p>
            </div>

            {/* Upgrade buttons */}
            {!purchases.unlockedAll && !purchases.progressSaving && (
              <div className={css` display: flex; gap: 10px; flex-wrap: wrap; `}>
                <UpgradeButton
                  label="Unlock All Units"
                  price="$10"
                  accent={theme.colors.tsBlue}
                  loading={checkoutLoading}
                  onClick={() => startCheckout("unlock_all")}
                />
                <UpgradeButton
                  label="Save Progress"
                  price="$3"
                  accent={theme.colors.success50}
                  loading={checkoutLoading}
                  onClick={() => startCheckout("progress_saving")}
                />
              </div>
            )}
            {purchases.unlockedAll && !purchases.progressSaving && (
              <UpgradeButton
                label="Add Progress Saving"
                price="$3"
                accent={theme.colors.success50}
                loading={checkoutLoading}
                onClick={() => startCheckout("progress_saving")}
              />
            )}
            {!purchases.unlockedAll && purchases.progressSaving && (
              <UpgradeButton
                label="Unlock All Units"
                price="$10"
                accent={theme.colors.tsBlue}
                loading={checkoutLoading}
                onClick={() => startCheckout("unlock_all")}
              />
            )}
          </div>
        )}
      </div>

      {/* Purchases */}
      {!purchases.loading && (purchases.unlockedAll || purchases.progressSaving || purchases.unlockedUnits.length > 0) && (
        <div className={cardStyle}>
          <h2 className={sectionTitle}>Purchases</h2>
          <table
            className={css`
              width: 100%;
              border-collapse: collapse;
              font-size: 14px;
            `}
          >
            <thead>
              <tr
                className={css`
                  th {
                    text-align: left;
                    padding: 6px 12px 10px 0;
                    color: ${theme.colors.grey50};
                    font-weight: 500;
                    font-size: 12px;
                    border-bottom: 1px solid ${theme.colors.grey25};
                  }
                `}
              >
                <th>Item</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {purchases.unlockedAll && (
                <PurchaseRow label="All Units Unlocked" date={purchases.unlockedAll_at} />
              )}
              {purchases.progressSaving && (
                <PurchaseRow label="Progress Saving" date={purchases.progressSaving_at} />
              )}
              {purchases.unlockedUnits.map((slug) => {
                const purchase = purchases.unlockedUnitPurchases.find(p => p.slug === slug);
                const unit = unitsBySlug.get(slug);
                return (
                  <PurchaseRow
                    key={slug}
                    label={unit?.name ?? slug}
                    href={unit ? unitUrl(unit) : undefined}
                    date={purchase?.purchasedAt ?? null}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Completed Units */}
      <div className={cardStyle}>
        <h2 className={sectionTitle}>
          Completed Units {!sessionsLoading && `(${completedUnits.length})`}
        </h2>
        {sessionsLoading ? (
          <p className={css` color: ${theme.colors.grey50}; margin: 0; font-size: 14px; `}>Loading...</p>
        ) : completedUnits.length === 0 ? (
          <p className={css` color: ${theme.colors.grey50}; margin: 0; font-size: 14px; `}>
            No completed units yet.{" "}
            <button
              onClick={() => navigate("/")}
              className={css`
                background: none;
                border: none;
                color: ${theme.colors.tsBlue};
                cursor: pointer;
                font-size: 14px;
                padding: 0;
                &:hover { text-decoration: underline; }
              `}
            >
              Start learning
            </button>
          </p>
        ) : (
          <UnitTable units={completedUnits} theme={theme} />
        )}
      </div>

      {/* In-Progress Units */}
      {(sessionsLoading || incompleteUnits.length > 0) && (
        <div className={cardStyle}>
          <h2 className={sectionTitle}>
            In Progress {!sessionsLoading && `(${incompleteUnits.length})`}
          </h2>
          {sessionsLoading ? (
            <p className={css` color: ${theme.colors.grey50}; margin: 0; font-size: 14px; `}>Loading...</p>
          ) : (
            <UnitTable units={incompleteUnits} theme={theme} />
          )}
        </div>
      )}
    </div>
  );
};

// ---------- Sub-components ----------

type UpgradeButtonProps = {
  label: string;
  price: string;
  accent: string;
  loading: boolean;
  onClick: () => void;
};

const UpgradeButton = ({ label, price, accent, loading, onClick }: UpgradeButtonProps) => (
  <button
    onClick={onClick}
    disabled={loading}
    className={css`
      padding: 8px 16px;
      background: transparent;
      color: ${accent};
      border: 1px solid ${accent};
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      opacity: ${loading ? 0.5 : 1};
      transition: all 0.15s;
      white-space: nowrap;
      &:hover:not(:disabled) {
        background: ${accent};
        color: #000;
      }
    `}
  >
    {label} — {price}
  </button>
);

const PurchaseRow = ({ label, href, date }: { label: string; href?: string; date?: Date | null }) => {
  const theme = useTheme();
  return (
    <tr
      className={css`
        td {
          padding: 10px 12px 10px 0;
          border-bottom: 1px solid ${theme.colors.grey25};
          color: ${theme.colors.grey80};
          font-size: 14px;
        }
        &:last-child td {
          border-bottom: none;
        }
      `}
    >
      <td>
        {href ? (
          <Link
            to={href}
            className={css`
              color: ${theme.colors.tsBlue};
              text-decoration: none;
              &:hover { text-decoration: underline; }
            `}
          >
            {label}
          </Link>
        ) : (
          label
        )}
      </td>
      <td className={css` color: ${theme.colors.grey50}; `}>
        {date ? date.toLocaleDateString() : "—"}
      </td>
      <td>
        <span
          className={css`
            color: ${theme.colors.success50};
            font-size: 12px;
            font-weight: 500;
          `}
        >
          Active
        </span>
      </td>
    </tr>
  );
};

type UnitTableProps = {
  units: ContextualizedUnit[];
  theme: ReturnType<typeof useTheme>;
};

const UnitTable = ({ units, theme }: UnitTableProps) => (
  <table
    className={css`
      width: 100%;
      border-collapse: collapse;
      font-size: 14px;
    `}
  >
    <thead>
      <tr
        className={css`
          th {
            text-align: left;
            padding: 6px 12px 10px 0;
            color: ${theme.colors.grey50};
            font-weight: 500;
            font-size: 12px;
            border-bottom: 1px solid ${theme.colors.grey25};
          }
        `}
      >
        <th>Unit</th>
        <th>Course</th>
        <th>Section</th>
      </tr>
    </thead>
    <tbody>
      {units.map((unit) => (
        <tr
          key={`${unit.section.course.ecosystem.slug}/${unit.section.course.slug}/${unit.section.slug}/${unit.id}`}
          className={css`
            td {
              padding: 10px 12px 10px 0;
              border-bottom: 1px solid ${theme.colors.grey25};
              font-size: 14px;
            }
            &:last-child td {
              border-bottom: none;
            }
            &:hover td {
              background: rgba(255,255,255,0.02);
            }
          `}
        >
          <td>
            <Link
              to={unitUrl(unit)}
              className={css`
                color: ${theme.colors.tsBlue};
                text-decoration: none;
                &:hover { text-decoration: underline; }
              `}
            >
              {unit.name}
            </Link>
          </td>
          <td className={css` color: ${theme.colors.grey70}; `}>
            {unit.section.course.name}
          </td>
          <td className={css` color: ${theme.colors.grey50}; `}>
            {unit.section.name}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

// ---------- Logged Out View ----------

const LoggedOutView = () => {
  const { signInWithGoogle } = useAuth();
  const theme = useTheme();

  return (
    <div
      className={css`
        max-width: 400px;
        margin: 60px auto;
        background: rgba(30, 30, 30, 0.7);
        backdrop-filter: blur(10px);
        border-radius: 10px;
        border: 1px solid rgba(100, 100, 100, 0.2);
        padding: 40px 32px;
        text-align: center;
      `}
    >
      <h2
        className={css`
          margin: 0 0 24px 0;
          font-size: 22px;
          color: ${theme.colors.grey100};
        `}
      >
        Sign in to CodeMastery
      </h2>

      <button
        type="button"
        onClick={signInWithGoogle}
        className={css`
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background-color: #ffffff;
          color: #757575;
          border: none;
          border-radius: 4px;
          padding: 12px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
          width: 100%;

          &:hover {
            background-color: #f5f5f5;
          }
        `}
      >
        <svg
          width="18"
          height="18"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 48 48"
        >
          <path
            fill="#FFC107"
            d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
          />
          <path
            fill="#FF3D00"
            d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
          />
          <path
            fill="#4CAF50"
            d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
          />
          <path
            fill="#1976D2"
            d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
          />
        </svg>
        Continue with Google
      </button>
    </div>
  );
};
